/**
 * One programme's curriculum history, opened from its portfolio row.
 *
 * Every approved revision the programme has had, plus any working change the
 * reader is allowed to see. A list rather than a nested table: it already
 * sits indented inside a table cell, and a second table there scrolls
 * sideways inside the first on a phone.
 *
 * @module components/portfolio/CurriculumHistory
 */

import React from "react";

import { amendmentLabel, changeCaseStatusLabel } from "@/components/shared/governance-labels";
import { curriculumRevisionLabel } from "@/components/shared/revision-labels";
import { lifecycleTone } from "@/components/shared/status-tones";
import {
  Alert,
  type AsyncState,
  Badge,
  Button,
  Heading,
  Icon,
  ListRow,
  MutedText,
  Panel,
} from "@/components/ui";
import { ListBones } from "@/components/ui/SkeletonParts";
import type { PortfolioProgrammeRevision, ProgrammeGovernanceSummary } from "@/platform";
import type { CurriculumChangeCase } from "@/server/types";
import { formatCalendarDate, formatDayOf } from "@/utils/calendar-dates";

import { uniqueApprovedRevisions } from "./portfolio-groups";
import {
  canBePublished,
  LIFECYCLE_LABEL,
  publicationReason,
  publicationState,
  TONE_BADGE,
} from "./revision-presentation";

/** Change cases still being worked, as opposed to ones already closed. */
const WORKING_STATUSES = new Set([
  "team-confirmation",
  "open",
  "submitted",
  "awaiting-approval",
  "changes-required",
]);

function changeCaseLabel(changeCase: CurriculumChangeCase): string {
  if (changeCase.kind === "revalidation") {
    return "Revalidation";
  }
  return amendmentLabel(changeCase.classification);
}

/** When a scope took or takes effect. */
function effectivePeriod(scope: PortfolioProgrammeRevision): string {
  return `Effective from ${formatCalendarDate(scope.effectiveFrom)}`;
}

/**
 * Which scope of a revision carries its management action.
 *
 * The first one that can be published, not the first row: a revision whose
 * earliest scope cannot be may have later ones that can, and taking the
 * first by position would hide the action entirely.
 *
 * Publication is not a tiebreak. `getTenantPortfolio` resolves
 * `latestPublication` from the revision id alone, so every scope of a revision
 * carries the same one and a search on it can only return the first scope,
 * which is already the fallback.
 */
export function manageableScope(
  scopes: readonly PortfolioProgrammeRevision[],
): PortfolioProgrammeRevision {
  return scopes.find(canBePublished) ?? scopes[0]!;
}

export interface CurriculumHistoryProps {
  programmeId: string;
  /** Every row the programme has, including ones the filters excluded. */
  revisions: readonly PortfolioProgrammeRevision[];
  /** The working change cases, or `null` where the reader may not see them. */
  state: AsyncState<ProgrammeGovernanceSummary | null>;
  canManage: boolean;
  canPublish: boolean;
  busy?: boolean;
  onManage: (revision: PortfolioProgrammeRevision) => void;
  onCompare: (programmeId: string, revisionId: string) => void;
  onChangeCaseOpen: (programmeId: string, changeCaseId: string) => void;
  onRetry: (programmeId: string) => void;
}

export function CurriculumHistory({
  programmeId,
  revisions,
  state,
  canManage,
  canPublish,
  busy = false,
  onManage,
  onCompare,
  onChangeCaseOpen,
  onRetry,
}: CurriculumHistoryProps): React.ReactElement {
  const approved = uniqueApprovedRevisions(revisions);
  // Compare needs two revisions to compare. One, and the control would open a
  // screen with nothing on one side of it.
  const comparable = approved.length > 1;
  const changeCases = (state.status === "ready" ? (state.data?.curriculumChangeCases ?? []) : [])
    .filter((entry) => entry.status !== "approved" && entry.status !== "replacement-created")
    .sort((left, right) => right.openedAt.localeCompare(left.openedAt));

  return (
    <Panel tone="inset" density="compact">
      {/* Level 2: the page is a `PageHeader` h1 over the table, and nothing
          renders a heading between them, so anything lower skips a level. The
          programme is in the name because every expanded row heads a section
          spelled the same way. */}
      <Heading level={2} size="md" className="flex items-center gap-2">
        <Icon name="clock-counter-clockwise" aria-hidden />
        {/* The whole name in one node, and the visible copy hidden from it:
            the name algorithm trims each text node, so a phrase assembled
            from two of them runs its words together. */}
        <span aria-hidden>Curriculum history</span>
        <span className="sr-only">
          {`Curriculum history for ${revisions[0]?.title ?? "this programme"}`}
        </span>
      </Heading>
      <MutedText size="caption" as="p" className="mt-1">
        Shows approved revisions and any working change you can see.
      </MutedText>

      {state.status === "loading" && (
        <div className="mt-3">
          <ListBones rows={3} label="Loading working revisions…" />
        </div>
      )}
      {state.status === "error" && (
        <Alert tone="warning" className="mt-3">
          {state.error.message}
          <Button size="sm" variant="outline" onClick={() => onRetry(programmeId)}>
            Try again
          </Button>
        </Alert>
      )}

      {changeCases.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {changeCases.map((changeCase) => {
            const working = WORKING_STATUSES.has(changeCase.status);
            return (
              <li key={changeCase.id}>
                <ListRow
                  data-testid={`portfolio-working-revision-${changeCase.id}`}
                  action={
                    working && changeCase.status !== "team-confirmation"
                      ? {
                          label: `Open ${changeCase.institutionalLabel ?? "unlabelled change"}`,
                          onClick: () => {
                            onChangeCaseOpen(programmeId, changeCase.id);
                          },
                        }
                      : undefined
                  }
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={working ? "primary" : "neutral"} size="sm" variant="soft">
                      {working ? "Working revision" : "Closed proposal"}
                    </Badge>
                    <span className="font-medium">
                      {changeCase.institutionalLabel ?? "Unlabelled change"}
                    </span>
                  </div>
                  <MutedText size="caption" as="div" className="mt-1">
                    {changeCaseLabel(changeCase)} · {changeCaseStatusLabel(changeCase.status)} ·
                    opened {formatDayOf(changeCase.openedAt)}
                  </MutedText>
                </ListRow>
              </li>
            );
          })}
        </ul>
      )}

      <ul className="mt-3 flex flex-col gap-2">
        {approved.map((scopes) => {
          const revision = scopes[0]!;
          const manageable = manageableScope(scopes);
          const published = Boolean(
            manageable.latestPublication && !manageable.latestPublication.withdrawnAt,
          );
          // Both badges read the scope the Manage action opens, not the first
          // row. Lifecycle is derived per scope, so a revision can be Current
          // for one intake and Approved future for the next, and mixing them
          // said "Current" beside a control that published a future revision.
          const publication = publicationState(manageable);
          const reason = publicationReason(manageable);
          return (
            <li key={revision.curriculumRevisionId}>
              <ListRow
                data-testid={`portfolio-revision-${revision.curriculumRevisionId}`}
                trailing={
                  <div className="flex gap-1">
                    {comparable && (
                      <Button
                        size="xs"
                        variant="ghost"
                        icon="git-diff"
                        aria-label={`Compare ${curriculumRevisionLabel(revision.revisionNumber).toLowerCase()}`}
                        onClick={() => {
                          onCompare(programmeId, revision.curriculumRevisionId);
                        }}
                        data-testid={`portfolio-compare-action-${revision.curriculumRevisionId}`}
                      >
                        {/* Icon only below `sm`: two labelled buttons in the
                            trailing corner left the revision's own label
                            about half the row. The name stays on the button. */}
                        <span className="max-sm:hidden">Compare</span>
                      </Button>
                    )}
                    {(canManage || (canPublish && (canBePublished(manageable) || published))) && (
                      <Button
                        size="xs"
                        variant="ghost"
                        icon="gear-six"
                        disabled={busy}
                        disabledReason="Wait for the publication change to finish"
                        aria-label={`Manage ${curriculumRevisionLabel(revision.revisionNumber).toLowerCase()}`}
                        onClick={() => {
                          onManage(manageable);
                        }}
                        data-testid={`portfolio-history-manage-${revision.curriculumRevisionId}`}
                      >
                        <span className="max-sm:hidden">Manage</span>
                      </Button>
                    )}
                  </div>
                }
              >
                <div className="font-medium">{revision.institutionalLabel}</div>
                <MutedText size="caption" as="div">
                  {curriculumRevisionLabel(revision.revisionNumber)}
                </MutedText>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <Badge
                    tone={TONE_BADGE[lifecycleTone(manageable.lifecycleStatus)]}
                    size="sm"
                    variant="soft"
                  >
                    {LIFECYCLE_LABEL[manageable.lifecycleStatus] ?? manageable.lifecycleStatus}
                  </Badge>
                  <Badge tone={publication.tone} size="sm" variant="soft">
                    {publication.label}
                  </Badge>
                </div>
                {reason && (
                  <MutedText size="caption" as="div" className="mt-1">
                    {reason}
                  </MutedText>
                )}
                <MutedText size="caption" as="div" className="mt-1">
                  {scopes.map((scope) => (
                    <div key={scope.effectiveFrom}>{effectivePeriod(scope)}</div>
                  ))}
                </MutedText>
              </ListRow>
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
